// Presentational chart primitives for the Dashboard: KPI cards, titled panels,
// and horizontal bar lists. Hand-rolled divs styled with Fluent tokens — the
// shapes are simple enough that a chart library would not earn its place (and
// zero deps keeps the app portable).
//
// `PairedColumns` and `LegendSwatch` were deleted on 2026-09-15 with the last
// panels that drew them: the weekly uploaded-vs-completed chart and the age-band
// legend both went in the dashboard rebuild, and nothing replaced them.
import type { ReactNode } from 'react'
import { Subtitle2, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'

const useStyles = makeStyles({
  panel: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '16px 20px',
    minWidth: 0,
  },
  // The title is always a single unwrapped line.
  panelHead: { marginBottom: '12px' },
  panelTitle: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kpi: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: '14px 16px',
    minWidth: 0,
  },
  kpiLabel: {
    color: tokens.colorNeutralForeground3,
    display: 'block',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kpiValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, lineHeight: '1.2' },
  kpiSub: { display: 'block', marginTop: '2px', color: tokens.colorNeutralForeground3 },
  kpiSubDanger: { color: tokens.colorPaletteRedForeground1 },
  barTable: { display: 'flex', flexDirection: 'column', gap: '6px' },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '132px minmax(0, 1fr) 64px',
    alignItems: 'center',
    columnGap: '8px',
  },
  barRowClickable: {
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusSmall,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  barLabel: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  barTrack: { display: 'flex', height: '14px', minWidth: 0 },
  barSeg: { height: '100%' },
  barValue: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  dimmed: { opacity: 0.35 },
})

// ---- Panel + KPI -------------------------------------------------------------
/**
 * A titled panel. Title and children — nothing else.
 *
 * IT USED TO TAKE `hint` AND `badge`, and both went on 2026-09-15 with their
 * last caller (owner: drop *"top 10 · invoices matching the filters now"* and
 * all similar text, and drop the *Import Control* / *References* pills). Twenty
 * panels each carrying a caption that restated the filter state, and two
 * carrying a role name, was noise a reader had to look past to reach the bars.
 * The props are gone rather than left unused so the next panel cannot quietly
 * bring the captions back.
 */
export function PanelCard({ title, children }: {
  title: string
  children: ReactNode
}) {
  const s = useStyles()
  return (
    <div className={s.panel}>
      <div className={s.panelHead}>
        <Subtitle2 className={s.panelTitle} title={title}>{title}</Subtitle2>
      </div>
      {children}
    </div>
  )
}

export function KpiCard({ label, value, sub, subDanger }: {
  label: string
  value: string
  sub?: string
  subDanger?: boolean
}) {
  const s = useStyles()
  return (
    <div className={s.kpi}>
      <Caption1 className={s.kpiLabel} title={label}>{label}</Caption1>
      <span className={s.kpiValue}>{value}</span>
      {sub ? <Caption1 className={mergeClasses(s.kpiSub, subDanger && s.kpiSubDanger)}>{sub}</Caption1> : null}
    </div>
  )
}

// ---- Horizontal bars ----------------------------------------------------------
interface BarSegment {
  color: string
  value: number
}

export interface BarRowData {
  key: string
  label: string
  /** Row magnitude — sets the bar length relative to the longest row. */
  total: number
  /** Right-aligned display of the total. */
  display: string
  /** Stacked segments; a single-color bar passes one segment. */
  segments: BarSegment[]
  title?: string
}

/** Label | bar | value rows. Clickable rows toggle a selection; when a
 * selection exists every other row dims (cross-filter affordance). */
export function HBarList({ rows, selectedKey, onSelect, valueWidth }: {
  rows: BarRowData[]
  selectedKey?: string | null
  onSelect?: (key: string) => void
  /** The value column's width in px, for a display longer than a count ("62% of 13"). Default 64. */
  valueWidth?: number
}) {
  const s = useStyles()
  const max = Math.max(...rows.map((r) => r.total), 1)
  return (
    <div className={s.barTable}>
      {rows.map((r) => {
        const dim = !!selectedKey && selectedKey !== r.key
        const widthPct = Math.max((r.total / max) * 100, r.total > 0 ? 1.5 : 0)
        return (
          <div
            key={r.key}
            className={mergeClasses(s.barRow, onSelect && s.barRowClickable, dim && s.dimmed)}
            style={valueWidth === undefined ? undefined : { gridTemplateColumns: `132px minmax(0, 1fr) ${valueWidth}px` }}
            onClick={onSelect ? () => onSelect(r.key) : undefined}
            title={r.title ?? r.label}
            role={onSelect ? 'button' : undefined}
          >
            <span className={s.barLabel}>{r.label}</span>
            <div className={s.barTrack} style={{ width: `${widthPct}%`, borderRadius: '3px', overflow: 'hidden' }}>
              {r.segments.filter((seg) => seg.value > 0).map((seg, i) => (
                <div key={i} className={s.barSeg} style={{ flexGrow: seg.value, backgroundColor: seg.color }} />
              ))}
            </div>
            <span className={s.barValue}>{r.display}</span>
          </div>
        )
      })}
    </div>
  )
}
